import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/shared/hooks';
import { User } from '@/shared/types/user.types';
import { registerAuthNavigate } from '@/shared/lib/authRedirect';

interface AuthContextType {
  user: User | null | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    registerAuthNavigate(navigate);
  }, [navigate]);

  const value: AuthContextType = useMemo(
    () => ({
      user: user || null,
      isLoading,
      isAuthenticated: !!user,
    }),
    [user, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};
